import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useLoaderData,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { SupportBadge } from "~/components/Layout/SupportBadge";
import { TopPanel } from "~/components/Layout/TopPanel";
import { LocationDrawer } from "~/components/LocationDrawer";
import supportPerson from "~/assets/support-person.png";
import { useEffect, useState } from "react";
import { IMAGE_SIZES, buildImageUrl, cn } from "~/lib/utils";
import Login from "@material-design-icons/svg/outlined/login.svg?react";
import { PinPlusIcon } from "~/icons/PinPlusIcon";
import { HeartIcon } from "~/icons/HeartIcon";
import { SupportIcon } from "~/icons/Support";
import { UserIcon } from "~/icons/UserIcon";
import { getFooter } from "~/lib/server-functions/get-footer";
import { getUserSession } from "~/lib/api/auth.api";
import { authQueries } from "~/lib/queries";
import { authClient, useAuthentication } from "~/lib/better-auth/client";
import { RichTextRenderer } from "~/components/RichTextRenderer";
import { DEFAULT_LOCALE, withLocale } from "~/lib/utils/with-locale";

export const Route = createFileRoute("/__main")({
  component: RouteComponent,
  beforeLoad: ({ location }) => {
    const path = location.pathname;

    const segments = path.split("/").filter(Boolean);
    const firstSegment = segments[0];

    const hasLocale = ["de"].includes(firstSegment);

    if (!hasLocale) {
      throw redirect({
        to: `/${DEFAULT_LOCALE}${path.startsWith("/") ? path : "/" + path}`,
        // Preserve query params and hash when adding the locale prefix.
        // `location.search` is the router's parsed search object.
        search: location.search as any,
        hash: location.hash as any,
      } as any);
    }
  },
  loader: async ({ context }) => {
    // Prefetch auth session server-side so it's available immediately
    const userSession = await getUserSession();
    context.queryClient.setQueryData(authQueries.user().queryKey, userSession);

    return await getFooter();
  },
});

function RouteComponent() {
  const footer = useLoaderData({ from: "/__main" });
  const location = useLocation();
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const { isAuthenticated } = useAuthentication();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Only use layoutWide for the search route
  const isSearchRoute = location.pathname === "/de/search";
  const isAuthRoute = /\/(sign-in|sign-up|forgot-password|reset-password)$/.test(
    location.pathname,
  );

  useEffect(() => {
    if (menuIsOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [menuIsOpen]);

  // Close menu when navigating to another page
  useEffect(() => {
    setMenuIsOpen(false);
  }, [location.pathname]);

  return (
    <>
      {!isAuthRoute && (
        <>
          <SupportBadge menuIsOpen={menuIsOpen} />
          <TopPanel
            layoutWide={isSearchRoute}
            menuIsOpen={menuIsOpen}
            onMenuToggle={setMenuIsOpen}
          />
          <div
            onClick={() => setMenuIsOpen(false)}
            className={cn(
              "fixed top-0 left-0 w-full h-full z-20",
              menuIsOpen ? "block" : "hidden",
            )}
          ></div>
          <div
            className={cn(
              "md:hidden bg-white py-6 fixed w-full left-0 right-0 top-(--top-panel-height) z-20 transition-transform duration-500",
              menuIsOpen
                ? ""
                : "-translate-y-[calc(100%+var(--top-panel-height))]",
            )}
          >
            <ul>
              {!isAuthenticated && (
                <>
                  <li>
                    <Link
                      to={withLocale("/sign-in")}
                      className="flex gap-4 px-4 py-3"
                    >
                      <Login className="fill-current text-black size-6" />
                      <span>
                        <span className="block font-medium">Login</span>
                        <span className="block font-light">
                          Für Nutzer, die Locations suchen
                        </span>
                      </span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      to={withLocale("/sign-in")}
                      className="flex gap-4 px-4 py-3"
                    >
                      <Login className="fill-current text-black size-6" />
                      <span>
                        <span className="block font-medium">Partner-Login</span>
                        <span className="block font-light">
                          Für Betreiber, die Locations verwalten
                        </span>
                      </span>
                    </Link>
                  </li>
                </>
              )}
              {isAuthenticated && (
                <li>
                  <Link to="/dashboard/user" className="flex gap-4 px-4 py-3">
                    <UserIcon className="fill-current text-black size-6" />
                    <span>
                      <span className="block font-medium">Benutzerkonto</span>
                      <span className="block font-light">
                        Benutzerkonto verwalten
                      </span>
                    </span>
                  </Link>
                </li>
              )}
              <li>
                {isAuthenticated ? (
                  <a
                    href={`${import.meta.env.VITE_ADMIN_URL}${withLocale("/locations")}`}
                    className="flex gap-4 px-4 py-3"
                  >
                    <PinPlusIcon className="fill-current text-black size-6" />
                    <span className="block font-medium">
                      Location eintragen
                    </span>
                  </a>
                ) : (
                  <Link
                    to={withLocale("/sign-in")}
                    className="flex gap-4 px-4 py-3"
                  >
                    <PinPlusIcon className="fill-current text-black size-6" />
                    <span className="block font-medium">
                      Location eintragen
                    </span>
                  </Link>
                )}
              </li>
              <li>
                {isAuthenticated ? (
                  <Link
                    to="/dashboard/watchlist"
                    className="flex gap-4 px-4 py-3"
                  >
                    <HeartIcon className="fill-current text-black size-6" />
                    <span className="block font-medium">Merkliste</span>
                  </Link>
                ) : (
                  <Link
                    to={withLocale("/sign-in")}
                    className="flex gap-4 px-4 py-3"
                  >
                    <HeartIcon className="fill-current text-black size-6" />
                    <span className="block font-medium">Merkliste</span>
                  </Link>
                )}
              </li>
              <li>
                <Link
                  to={withLocale("/sign-in")}
                  className="flex gap-4 px-4 py-3"
                >
                  <SupportIcon className="fill-current text-black size-6" />
                  <span className="block font-medium">Support</span>
                </Link>
              </li>
              {isAuthenticated && (
                <li>
                  <button
                    type="button"
                    className="flex gap-4 px-4 py-3 w-full text-left"
                    onClick={async () => {
                      await authClient.signOut();
                      queryClient.invalidateQueries({
                        queryKey: ["auth", "user"],
                      });
                      navigate({ to: withLocale("/sign-in") });
                    }}
                  >
                    <Login className="fill-current text-black size-6" />
                    <span>
                      <span className="block font-medium">Logout</span>
                      <span className="block font-light">
                        Aus diesem Account abmelden
                      </span>
                    </span>
                  </button>
                </li>
              )}
            </ul>
          </div>
        </>
      )}

      {isAuthRoute ? (
        <Outlet />
      ) : (
        <div className="layout-content-grid max-w-(--layout-page-max-width) mx-auto ring ring-gray-200 shadow-2xl pb-[calc(var(--location-drawer-height)+var(--location-mobile-actions-height))]">
          <Outlet />
          <LocationDrawer />
          <Footer
            supportText={footer?.data.supportText}
            phonenumber={footer?.data.phonenumber}
            email={footer?.data.email}
            brandMessage={footer?.data.brandMessage}
            logo={
              footer?.data.logoIdentifier
                ? buildImageUrl(footer.data.logoIdentifier, {
                  size: IMAGE_SIZES.logo,
                })
                : ""
            }
            linksUpper={footer?.data.linksUpper || []}
            linksLower={footer?.data.linksLower || []}
          />
        </div>
      )}
    </>
  );
}

interface FooterProps {
  logo?: string;
  supportText?: string;
  phonenumber?: string;
  email?: string;
  brandMessage?: string;
  /** When set (e.g. on advertiser domain), footer links use this as origin so they point to the main site. */
  linksBaseUrl?: string;
  linksUpper?: Array<{
    uid: number;
    title: string | null;
    slug: string | null;
  }>;
  linksLower?: Array<{
    uid: number;
    title: string | null;
    slug: string | null;
  }>;
}

function footerLinkHref(slug: string | null, baseUrl?: string): string {
  const path = slug || "#";
  if (!baseUrl) return path;
  const base = baseUrl.replace(/\/$/, "");
  return path === "#"
    ? path
    : `${base}${path.startsWith("/") ? path : `/${path}`}`;
}

export const Footer = ({
  logo = "",
  supportText = "",
  phonenumber = "",
  email = "",
  brandMessage = "",
  linksBaseUrl,
  linksUpper = [],
  linksLower = [],
}: FooterProps) => {
  return (
    <footer className="layout-place-full-width layout-content-grid border-t-10 border-black pt-11 pb-6">
      <div className="flex flex-col lg:flex-row gap-8 justify-between w-full">
        <div className="flex flex-col gap-3">
          <ul className="grid grid-cols-4 gap-8">
            {linksUpper.map((link) => (
              <li key={link.uid}>
                <a href={footerLinkHref(link.slug, linksBaseUrl)}>
                  {link.title || ""}
                </a>
              </li>
            ))}
          </ul>
          <ul className="grid grid-cols-4 gap-8">
            {linksLower.map((link) => (
              <li key={link.uid}>
                <a
                  className="text-sm"
                  href={footerLinkHref(link.slug, linksBaseUrl)}
                >
                  {link.title || ""}
                </a>
              </li>
            ))}
          </ul>
        </div>
        <div className="max-w-[600px] flex flex-wrap gap-6 items-start">
          <div className="flex gap-6 items-start flex-nowrap">
            <img src={logo} alt="" width={180} height={36} />
            <RichTextRenderer
              additionalClasses="text-sm"
              richText={brandMessage}
            />
          </div>
          <div className="w-full flex flex-col items-end">
            <div className="flex flex-col items-center">
              <img
                src={supportPerson}
                alt=""
                width={80}
                height={80}
                className="-mb-7 relative z-0 mr-[200px]"
              />
              <div className="bg-black text-white py-2 px-10 font-semibold text-xl rounded-md w-fit">
                LG<span className="text-primary">24</span> Support
              </div>

              <div className="bg-neutral-lightest shadow-[0_4px_4px_0_rgba(0,0,0,0.25)] p-6 rounded-lg">
                <p className="font-bold">
                  {supportText.length > 0
                    ? supportText
                    : "Sie haben Fragen, wir antworten."}
                </p>
                <a
                  className="flex underline gap-3 mt-4"
                  href={`tel:${phonenumber}`}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4.11902 3.34473H7.61902C7.80275 3.34474 7.93038 3.39945 8.03503 3.49805C8.12854 3.58614 8.20705 3.70059 8.2655 3.85156L8.31726 4.01465L8.9823 7.02832C9.01497 7.26011 9.00744 7.44511 8.97253 7.5918C8.94139 7.72276 8.8796 7.83104 8.78015 7.92578L8.77332 7.93164L6.25574 10.415L5.97644 10.6904L6.17664 11.0264C6.60337 11.7435 7.05685 12.416 7.53699 13.042C8.01981 13.6715 8.55599 14.2696 9.14441 14.8369C9.75779 15.4701 10.399 16.0453 11.0682 16.5605C11.7434 17.0804 12.4488 17.5353 13.1844 17.9248L13.5155 18.0996L13.7762 17.832L16.1815 15.3643L16.1913 15.3535C16.3309 15.2021 16.4775 15.111 16.6337 15.0645C16.7647 15.0256 16.8944 15.012 17.0253 15.0215L17.1571 15.0391L19.9774 15.6631C20.1431 15.7092 20.2749 15.7806 20.3817 15.875L20.4813 15.9775C20.5981 16.1186 20.6552 16.2764 20.6552 16.4707V19.8809C20.6552 20.1178 20.581 20.2895 20.4374 20.4316C20.2909 20.5766 20.1181 20.6494 19.8866 20.6494C18.0114 20.6494 16.0911 20.1991 14.1219 19.2842C12.1538 18.3698 10.3229 17.0691 8.62976 15.376C7.0427 13.7889 5.79967 12.0798 4.89539 10.249L4.71863 9.88086C3.80159 7.90938 3.35046 5.99008 3.35046 4.11914C3.35048 3.94128 3.39174 3.79881 3.47351 3.67773L3.56921 3.56348C3.71327 3.41863 3.88502 3.34473 4.11902 3.34473ZM4.06628 4.5752C4.09721 5.28818 4.20923 6.0365 4.39929 6.81836C4.59086 7.60639 4.89058 8.47618 5.2948 9.4248L5.58386 10.1035L6.1073 9.58301L8.12683 7.5752L8.32019 7.38281L8.26257 7.11621L7.69421 4.44922L7.60925 4.05371H4.04382L4.06628 4.5752ZM19.9462 16.377L19.5477 16.2949L17.0624 15.7803L16.7909 15.7246L16.5985 15.9248L14.6356 17.9824L14.1542 18.4863L14.787 18.7812C15.4818 19.1045 16.2353 19.3664 17.0448 19.5693C17.8555 19.7725 18.6477 19.8945 19.4208 19.9336L19.9462 19.96V16.377Z"
                      fill="#060606"
                      stroke="#060606"
                    />
                  </svg>
                  {phonenumber}
                </a>
                <a
                  className="flex underline gap-3 mt-3"
                  href={`mailto:${email}`}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M3.05359 5.74219V18.9463H20.9462V6.13867L20.1708 6.64844L12.2147 11.8867C12.1544 11.9184 12.102 11.944 12.0565 11.9619C12.0534 11.9629 12.0366 11.9678 11.9999 11.9678C11.9626 11.9678 11.946 11.9628 11.9432 11.9619C11.8975 11.9439 11.8448 11.9186 11.7841 11.8867L4.05359 6.7959V6.39648L11.7264 11.4219L11.9999 11.6016L12.2733 11.4229L20.62 5.97266L21.6307 5.31152C21.6464 5.38933 21.6551 5.46973 21.6552 5.55371V18.4463C21.6552 18.7675 21.5429 19.041 21.2938 19.2891C21.0449 19.5371 20.7701 19.6494 20.4462 19.6494H3.55359C3.23187 19.6494 2.95869 19.5371 2.71082 19.2891L2.6239 19.1953C2.43623 18.9725 2.35046 18.7279 2.35046 18.4463V5.55371C2.35048 5.46519 2.35953 5.38047 2.37683 5.29883L3.05359 5.74219ZM3.55359 4.34473H20.4462C20.7696 4.34473 21.0438 4.4579 21.2928 4.70703H21.2938C21.4035 4.81669 21.485 4.93216 21.5438 5.05371H2.46082C2.5195 4.93172 2.60127 4.81595 2.71082 4.70605H2.71179C2.95976 4.45718 3.23251 4.34476 3.55359 4.34473Z"
                      fill="#060606"
                      stroke="#060606"
                    />
                  </svg>
                  {email}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
